import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, FileText, Mail, Globe } from 'lucide-react';

const TermsPage = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black/95 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white hover:text-primary transition-colors" data-testid="terms-back-link">
            <ArrowLeft className="w-5 h-5" />
            <span>Volver</span>
          </Link>
          <Link to="/" className="flex items-center gap-2" data-testid="terms-brand-link">
            <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
              <span className="text-black font-bold text-sm">P</span>
            </div>
            <span className="text-white font-bold">PLEYADES</span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-primary/10 rounded-xl">
              <FileText className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white" data-testid="terms-title">Términos y Condiciones</h1>
              <p className="text-zinc-400" data-testid="terms-last-updated">Última actualización: 29 de septiembre de 2026</p>
            </div>
          </div>
        </div>

        <div className="prose prose-invert prose-zinc max-w-none">
          {/* Section 1 */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-primary mb-4">1. Identificación</h2>
            <div className="bg-zinc-900/50 rounded-xl p-6 border border-white/5">
              <ul className="space-y-2 text-zinc-300">
                <li className="flex items-center gap-2">
                  <span className="text-zinc-500">Titular:</span>
                  <span className="text-white font-medium">Pleyades Parking</span>
                </li>
                <li className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-zinc-500" />
                  <span className="text-zinc-500">Sitio web:</span>
                  <a href="https://pleyadesparking.es" className="text-primary hover:underline" data-testid="terms-website-link">pleyadesparking.es</a>
                </li>
                <li className="flex flex-wrap items-center gap-2">
                  <Mail className="w-4 h-4 shrink-0 text-zinc-500" />
                  <span className="text-zinc-500">Email de contacto:</span>
                  <a href="mailto:julioviv@pleyadesparking.es" className="max-w-full break-all text-primary hover:underline" data-testid="terms-contact-email-link">julioviv@pleyadesparking.es</a>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-zinc-500">Servicio:</span>
                  <span className="text-zinc-300">Valet parking con recogida y entrega del vehículo en aeropuerto (según punto acordado con el cliente).</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Section 2 */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-primary mb-4">2. Objeto y aceptación</h2>
            <p className="text-zinc-300 leading-relaxed">
              Estos Términos regulan el acceso y uso del sitio web y la contratación del servicio de valet parking. 
              Al reservar, el cliente declara haber leído y aceptado estos Términos y Condiciones.
            </p>
          </section>

          {/* Section 3 */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-primary mb-4">3. Cómo funciona el servicio</h2>
            <ol className="space-y-4 text-zinc-300">
              <li className="flex gap-4">
                <span className="flex-shrink-0 w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center text-primary font-bold">1</span>
                <span>El cliente realiza una reserva indicando fechas/horas.</span>
              </li>
              <li className="flex gap-4">
                <span className="flex-shrink-0 w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center text-primary font-bold">2</span>
                <span>En la fecha/hora de entrada, un trabajador recoge el vehículo en el punto acordado en el aeropuerto.</span>
              </li>
              <li className="flex gap-4">
                <span className="flex-shrink-0 w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center text-primary font-bold">3</span>
                <span>El vehículo se traslada a un aparcamiento gestionado por Pleyades Parking.</span>
              </li>
              <li className="flex gap-4">
                <span className="flex-shrink-0 w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center text-primary font-bold">4</span>
                <span>A la vuelta, se realiza la entrega del vehículo al cliente en el punto acordado.</span>
              </li>
            </ol>
          </section>

          {/* Section 4 */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-primary mb-4">4. Requisitos de reserva</h2>
            <ul className="space-y-3 text-zinc-300">
              <li className="flex items-start gap-3">
                <span className="text-primary">•</span>
                <span><strong className="text-white">Reserva mínima:</strong> 6 horas.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary">•</span>
                <span>El cliente debe facilitar datos veraces y completos: nombre, email, teléfono y, cuando aplique, matrícula y/o indicaciones de vuelo/punto de encuentro.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary">•</span>
                <span>El cliente debe acudir al punto acordado a la hora indicada o comunicar retrasos lo antes posible.</span>
              </li>
            </ul>
          </section>

          {/* Section 5 */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-primary mb-4">5. Precios, impuestos y pagos</h2>
            <ul className="space-y-3 text-zinc-300">
              <li className="flex items-start gap-3">
                <span className="text-primary">•</span>
                <span>El precio se calcula según el tiempo reservado y servicios adicionales, si los hubiera.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary">•</span>
                <div>
                  <span className="font-medium text-white">Métodos de pago disponibles:</span>
                  <ul className="mt-2 ml-4 space-y-2">
                    <li><strong className="text-primary">Pago online (Stripe):</strong> la reserva figura como PAGADO.</li>
                    <li><strong className="text-primary">Cobro en el parking:</strong> (efectivo u otro método presencial) la reserva figura como "Cobro en el parking".</li>
                    <li><strong className="text-primary">Canales/partners:</strong> (Parkos, ParkVia, Onepark, Looking4Parking, Free2Move, etc.) el pago puede estar PAGADO o "Cobro en el parking", según condiciones del canal.</li>
                  </ul>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary">•</span>
                <span>En caso de modificación de horas, el sistema recalculará la diferencia y podrá requerir pago adicional o generar un abono si corresponde.</span>
              </li>
            </ul>
          </section>

          {/* Section 6 */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-primary mb-4">6. Modificación de reserva</h2>
            <p className="text-zinc-300 mb-4">El cliente puede solicitar cambios de horario. La modificación:</p>
            <ul className="space-y-2 text-zinc-300 ml-4">
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                <span>está sujeta a disponibilidad,</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                <span>puede implicar recalcular el precio,</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                <span>puede requerir confirmación adicional por parte del equipo operativo (según el caso).</span>
              </li>
            </ul>
          </section>

          {/* Section 7 */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-primary mb-4">7. Cancelaciones y reembolsos</h2>
            <p className="text-zinc-300 mb-4">La política de cancelación puede variar según:</p>
            <ul className="space-y-2 text-zinc-300 ml-4 mb-4">
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                <span>si la reserva es directa o proviene de un canal,</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                <span>si el pago fue online o en el parking.</span>
              </li>
            </ul>
            <div className="bg-zinc-900/50 rounded-xl p-6 border border-white/5">
              <p className="text-white font-medium mb-2">Regla general:</p>
              <ul className="space-y-2 text-zinc-300">
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Si corresponde reembolso, se tramitará por el mismo medio de pago cuando sea posible (por ejemplo, Stripe).</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Para reservas de canal, pueden aplicar las condiciones del canal (reembolsos, comisiones o plazos).</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Section 8 */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-primary mb-4">8. Entrega y recogida del vehículo</h2>
            <ul className="space-y-3 text-zinc-300">
              <li className="flex items-start gap-3">
                <span className="text-primary">•</span>
                <span>El cliente deberá presentar la confirmación de reserva (email/QR) y seguir las instrucciones de entrega/recogida.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary">•</span>
                <span>Pleyades Parking podrá verificar el estado visible del vehículo y registrar incidencias operativas (por ejemplo, fotografías internas).</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary">•</span>
                <span>El cliente debe informar sobre particularidades del vehículo (llaves especiales, batería, alarmas, etc.).</span>
              </li>
            </ul>
          </section>

          {/* Section 9 - Movilización, Traslado y Custodia */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-primary mb-4">9. Movilización, Traslado y Custodia de Vehículos</h2>
            <div className="space-y-4 text-zinc-300">
              <p className="leading-relaxed">
                Al contratar los servicios de Pléyades Parking, el cliente autoriza expresamente a la empresa y a su personal debidamente autorizado a conducir, movilizar, estacionar y trasladar el vehículo entregado cuando resulte necesario para la correcta prestación del servicio.
              </p>
              <p className="leading-relaxed">
                Los vehículos recibidos por Pléyades Parking podrán ser movilizados entre diferentes campas, aparcamientos, instalaciones o emplazamientos gestionados, explotados o autorizados por la empresa, según criterios de disponibilidad, capacidad operativa, seguridad, mantenimiento, logística o cualquier otra necesidad relacionada con la prestación del servicio.
              </p>
              <p className="leading-relaxed">
                El cliente reconoce y acepta que la ubicación exacta del vehículo podrá variar durante el período de custodia, formando dichos desplazamientos parte inherente y necesaria del servicio de valet parking contratado.
              </p>
              <p className="leading-relaxed">
                Asimismo, el cliente acepta que el vehículo podrá recorrer una distancia variable durante las operaciones de recogida, entrega, estacionamiento, reubicación o traslado entre instalaciones, sin que ello constituya un uso indebido del vehículo, siempre que dichos desplazamientos se realicen en el marco de la prestación del servicio contratado.
              </p>
              <div className="bg-zinc-900/50 rounded-xl p-6 border border-primary/20 mt-4">
                <p className="text-white font-medium flex items-center gap-2 mb-2">
                  <span className="text-primary">✓</span>
                  Compromiso de Pléyades Parking:
                </p>
                <p className="text-zinc-300">
                  Todos los desplazamientos serán efectuados exclusivamente por personal autorizado y con las debidas medidas de diligencia, seguridad y cuidado exigibles para la correcta custodia del vehículo.
                </p>
              </div>
            </div>
          </section>

          {/* Section 10 */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-primary mb-4">10. Responsabilidad y limitaciones</h2>
            <ul className="space-y-3 text-zinc-300">
              <li className="flex items-start gap-3">
                <span className="text-primary">•</span>
                <span>Pleyades Parking se compromete a prestar el servicio con diligencia profesional.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary">•</span>
                <div>
                  <span className="font-medium text-white">No nos hacemos responsables de:</span>
                  <ul className="mt-2 ml-4 space-y-1">
                    <li>- objetos personales dejados dentro del vehículo,</li>
                    <li>- fallos mecánicos/electrónicos preexistentes,</li>
                    <li>- daños derivados de fuerza mayor o hechos ajenos al control razonable.</li>
                  </ul>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary">•</span>
                <span>En caso de incidencia, el cliente debe comunicarlo lo antes posible para su gestión.</span>
              </li>
            </ul>
          </section>

          <section id="restricciones-madrid" className="mb-10 scroll-mt-24" aria-labelledby="madrid-restrictions-heading" data-testid="terms-section-madrid-restrictions">
            <h3 id="madrid-restrictions-heading" className="text-lg font-bold text-primary mb-4" data-testid="terms-madrid-restrictions-heading">
              10.1. Distintivo ambiental y restricciones de circulación en Madrid
            </h3>
            <div className="space-y-4 text-zinc-300">
              <p className="leading-relaxed" data-testid="terms-madrid-client-obligations">
                Es responsabilidad del cliente comprobar, antes de contratar el servicio y entregar el vehículo, que este cumple las restricciones de acceso y circulación vigentes en Madrid, incluidas las Zonas de Bajas Emisiones (ZBE). Esta obligación comprende verificar el distintivo ambiental exigible y disponer de las autorizaciones o exenciones necesarias, especialmente en el caso de vehículos sin distintivo ambiental, así como comunicar a Pleyades Parking cualquier limitación que afecte a los desplazamientos necesarios para prestar el servicio.
              </p>
              <p className="leading-relaxed" data-testid="terms-madrid-fines-liability">
                <strong className="text-white">Pleyades Parking no se hace responsable de las multas o sanciones</strong> derivadas de circular con un vehículo sin el distintivo ambiental exigible o sin las autorizaciones necesarias bajo las restricciones de Madrid, cuando dicho incumplimiento sea imputable al cliente. En estos casos, la responsabilidad y el pago de las sanciones corresponderán al cliente.
              </p>
              <p className="leading-relaxed" data-testid="terms-madrid-liability-limits">
                La contratación del servicio no constituye una autorización para circular por zonas restringidas ni garantiza una exención de las restricciones aplicables. Esta cláusula se aplicará conforme a la normativa vigente y no excluye las responsabilidades que legalmente correspondan a Pleyades Parking.
              </p>
            </div>
          </section>

          {/* Section 11 */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-primary mb-4">11. Conducta y uso del sitio web</h2>
            <p className="text-zinc-300">
              Queda prohibido el uso del sitio con fines ilícitos, fraudulentos o que puedan dañar la plataforma o a terceros.
            </p>
          </section>

          {/* Section 12 */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-primary mb-4">12. Comunicaciones</h2>
            <ul className="space-y-3 text-zinc-300">
              <li className="flex items-start gap-3">
                <span className="text-primary">•</span>
                <span>El cliente recibirá emails operativos (confirmación, cambios, avisos del servicio).</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary">•</span>
                <span>Los emails promocionales solo se enviarán si el cliente ha otorgado consentimiento expreso (opt-in).</span>
              </li>
            </ul>
          </section>

          {/* Section 13 */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-primary mb-4">13. Protección de datos</h2>
            <p className="text-zinc-300">
              El tratamiento de datos personales se regula en la{' '}
              <Link to="/privacidad" className="text-primary hover:underline font-medium" data-testid="terms-data-privacy-link">Política de Privacidad</Link>.
            </p>
          </section>

          {/* Section 14 - Uso de Inteligencia Artificial */}
          <section className="mb-10" data-testid="terms-section-ai">
            <h2 className="text-xl font-bold text-primary mb-4">14. Uso de Inteligencia Artificial</h2>
            <div className="space-y-4 text-zinc-300">
              <p className="leading-relaxed">
                La plataforma podrá utilizar sistemas de Inteligencia Artificial (IA) para automatizar procesos, generar contenido, asistir al usuario, realizar recomendaciones, analizar información y mejorar la prestación de los servicios ofrecidos.
              </p>
              <p className="leading-relaxed">
                Al utilizar la plataforma, el usuario reconoce y acepta las siguientes condiciones:
              </p>

              <div className="bg-zinc-900/50 rounded-xl p-6 border border-white/5 space-y-4">
                <div>
                  <p className="text-white font-semibold mb-1">Transparencia</p>
                  <p className="text-zinc-300 leading-relaxed">
                    Cuando el usuario interactúe con funcionalidades basadas en Inteligencia Artificial, será informado de ello cuando resulte legalmente exigible.
                  </p>
                </div>

                <div>
                  <p className="text-white font-semibold mb-1">Carácter asistencial</p>
                  <p className="text-zinc-300 leading-relaxed">
                    Las respuestas, recomendaciones, análisis o contenidos generados mediante Inteligencia Artificial tienen carácter meramente informativo y de asistencia. No constituyen asesoramiento profesional, jurídico, financiero, fiscal, médico ni de ninguna otra naturaleza especializada.
                  </p>
                </div>

                <div>
                  <p className="text-white font-semibold mb-1">Revisión humana</p>
                  <p className="text-zinc-300 leading-relaxed">
                    El usuario es el único responsable de revisar, verificar y validar cualquier información, documento, cálculo o contenido generado por la Inteligencia Artificial antes de utilizarlo o tomar decisiones basadas en él.
                  </p>
                </div>

                <div>
                  <p className="text-white font-semibold mb-1">Limitación de responsabilidad</p>
                  <p className="text-zinc-300 leading-relaxed">
                    La empresa no garantiza que los resultados generados por la Inteligencia Artificial sean completos, exactos, actualizados o libres de errores, por lo que no asumirá responsabilidad por los daños o perjuicios derivados de su utilización.
                  </p>
                </div>

                <div>
                  <p className="text-white font-semibold mb-1">Tratamiento de datos</p>
                  <p className="text-zinc-300 leading-relaxed">
                    Los datos introducidos por el usuario podrán ser procesados por sistemas de Inteligencia Artificial con la única finalidad de prestar el servicio solicitado, mejorar la experiencia del usuario y optimizar el funcionamiento de la plataforma, todo ello conforme a la normativa vigente en materia de protección de datos y a la{' '}
                    <Link to="/privacidad" className="text-primary hover:underline font-medium" data-testid="terms-ai-privacy-link">Política de Privacidad</Link>.
                  </p>
                </div>

                <div>
                  <p className="text-white font-semibold mb-1">Uso responsable</p>
                  <p className="text-zinc-300 leading-relaxed">
                    El usuario se compromete a no utilizar las funcionalidades de Inteligencia Artificial para desarrollar actividades ilícitas, fraudulentas, discriminatorias, difamatorias, que vulneren derechos de terceros o que incumplan la legislación aplicable.
                  </p>
                </div>

                <div>
                  <p className="text-white font-semibold mb-1">Supervisión y seguridad</p>
                  <p className="text-zinc-300 leading-relaxed">
                    La empresa podrá supervisar el funcionamiento de los sistemas de Inteligencia Artificial, aplicar controles de seguridad, limitar determinadas funcionalidades o intervenir manualmente cuando resulte necesario para garantizar la calidad del servicio, la seguridad de la plataforma o el cumplimiento de la normativa aplicable.
                  </p>
                </div>

                <div>
                  <p className="text-white font-semibold mb-1">Mejora continua</p>
                  <p className="text-zinc-300 leading-relaxed">
                    La empresa podrá actualizar, modificar, sustituir o eliminar las funcionalidades de Inteligencia Artificial en cualquier momento con el fin de mejorar su rendimiento, seguridad, precisión o adaptación a cambios legales o tecnológicos.
                  </p>
                </div>

                <div>
                  <p className="text-white font-semibold mb-1">Contenido generado mediante IA</p>
                  <p className="text-zinc-300 leading-relaxed">
                    Cuando la plataforma genere textos, imágenes, documentos u otros contenidos mediante Inteligencia Artificial, dichos contenidos podrán requerir revisión por parte del usuario antes de su utilización o difusión.
                  </p>
                </div>

                <div>
                  <p className="text-white font-semibold mb-1">Cumplimiento normativo</p>
                  <p className="text-zinc-300 leading-relaxed">
                    La empresa desarrollará y utilizará los sistemas de Inteligencia Artificial procurando cumplir la normativa aplicable de la Unión Europea, incluido el Reglamento de Inteligencia Artificial (AI Act), así como cualquier otra disposición legal vigente en materia de protección de datos, transparencia, seguridad y derechos fundamentales.
                  </p>
                </div>

                <div>
                  <p className="text-white font-semibold mb-1">Aceptación</p>
                  <p className="text-zinc-300 leading-relaxed">
                    El uso continuado de la plataforma implica la aceptación expresa de las presentes condiciones relativas al uso de Inteligencia Artificial.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 15 */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-primary mb-4">15. Legislación aplicable y jurisdicción</h2>
            <p className="text-zinc-300">
              Estos Términos se rigen por la legislación española. Para cualquier controversia, las partes se someten a los juzgados y tribunales competentes según la normativa aplicable.
            </p>
          </section>

          {/* Section 16 */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-primary mb-4">16. Cambios en los Términos</h2>
            <p className="text-zinc-300">
              Podemos actualizar estos Términos para reflejar cambios operativos o legales. La versión vigente estará publicada en el sitio web.
            </p>
          </section>
        </div>

        {/* Footer links */}
        <div className="mt-12 pt-8 border-t border-white/10 flex flex-wrap gap-4 justify-center">
          <Link to="/privacidad" className="text-primary hover:underline" data-testid="terms-footer-privacy-link">Política de Privacidad</Link>
          <span className="text-zinc-600">|</span>
          <Link to="/" className="text-primary hover:underline" data-testid="terms-footer-home-link">Volver al inicio</Link>
          <span className="text-zinc-600">|</span>
          <a href="mailto:julioviv@pleyadesparking.es" className="text-primary hover:underline" data-testid="terms-footer-contact-link">Contacto</a>
        </div>
      </main>
    </div>
  );
};

export default TermsPage;
